import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL, categories } from "@/mocks/categories";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  const goToSection = (id: string) => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b-2 border-background-300 bg-background-50"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg"></i>
          </span>
          <span className={`font-heading text-xl font-extrabold tracking-tight ${solid ? "text-foreground-950" : "text-background-50"}`}>
            {t("site.name")}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              solid ? "text-foreground-800 hover:text-primary-700" : "text-background-50 hover:text-background-50"
            }`}
          >
            {t("nav.home")}
          </Link>
          <Link
            to="/usfans-spreadsheet-guide"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              solid ? "text-foreground-800 hover:text-primary-700" : "text-background-50 hover:text-background-50"
            }`}
          >
            {t("nav.guide")}
          </Link>
          <button
            type="button"
            onClick={() => goToSection("categories")}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              solid ? "text-foreground-800 hover:text-primary-700" : "text-background-50 hover:text-background-50"
            }`}
          >
            {t("nav.categories")}
          </button>
          <button
            type="button"
            onClick={() => goToSection("faq")}
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
              solid ? "text-foreground-800 hover:text-primary-700" : "text-background-50 hover:text-background-50"
            }`}
          >
            {t("nav.faq")}
          </button>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600 md:inline-flex"
          >
            {t("nav.buy")}
          </a>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-md md:hidden ${
              solid ? "text-foreground-950" : "text-background-50"
            }`}
            aria-label="Menu"
          >
            <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100">
              {t("nav.home")}
            </Link>
            <Link to="/usfans-spreadsheet-guide" className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100">
              {t("nav.guide")}
            </Link>
            <button type="button" onClick={() => goToSection("faq")} className="rounded-md px-3 py-2.5 text-left text-sm font-semibold text-foreground-800 hover:bg-background-100">
              {t("nav.faq")}
            </button>
          </div>

          <div className="mt-3 border-t border-background-200 pt-3">
            <p className="px-3 text-xs font-bold uppercase tracking-wide text-foreground-500">{t("nav.categories")}</p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <a
                  key={c.id}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded bg-background-100">
                    <img src={c.image} alt="" className="h-full w-full object-cover object-top" />
                  </span>
                  <span className="truncate">{t(`cat.${c.id}`)}</span>
                </a>
              ))}
            </div>
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-3 block whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-center text-sm font-bold text-background-50"
          >
            {t("nav.buy")}
          </a>
        </div>
      )}
    </header>
  );
}